import Utils from '../utils/Utils';
import AuthUtils from './AuthUtils';
import { AuthStorageWrapper } from './AuthStorageWrapper';
import { RequestTokenResponse } from './dsAuth/parser/RequestTokenResponseParser';
import {
	SecondaryTokenTableEntry,
	PrimaryTokenTableEntry,
	StoreSecondaryTokenFunctionParams,
} from './dsAuth/data/AuthTypes';
import { storageConstants } from './AuthConstants';
import { logConstants } from '../utils/Constants';
import { Logger } from './Logger';
import ctxsNative from './ctxsNative';
import DSAuth from './dsAuth/DSAuth';
import AuthMan from './AuthMan';
import { EventType, PrimaryTokensUpdatedEvent } from './Events/EventTypes';
import { v4 } from 'uuid';
import { environment } from './environment';
import { config } from './AuthConfig';

const utils = Utils.getInstance();

export type PrimaryTokensUpdateReason = 'login' | 'refresh' | 'native';
export default class TokenManager {
	private static instance: TokenManager;

	/// <summary>
	/// Create a new instance of TokenManager if not already present.
	/// </summary>
	static getInstance(): TokenManager {
		if (!TokenManager.instance) {
			TokenManager.instance = new TokenManager();
		}
		return TokenManager.instance;
	}

	/// <summary>
	/// get secondary token if present in the db for given url (param secondaryUrl)
	/// </summary>
	/// <param name="secondaryUrl">url for which we need a token</param>
	/// <param name="authDomain">if url is authdomain specific, this param contains the authdomain extracted from the url or request headers</param>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="id">request id</param>
	/// <returns>object containing secondary token</returns>
	async getCachedSecondaryTokenForURL(
		secondaryUrl: string,
		authDomain: string,
		referenceUri: string,
		id: string,
		decrypt = true
	): Promise<SecondaryTokenTableEntry | null> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		let secondaryToken: SecondaryTokenTableEntry;
		secondaryUrl = utils.removeQueryStringAndHashFromUrl(secondaryUrl);
		if (authDomain) {
			//URL is authDomain specific, hence checking for token matching full url + same authDomain and referenceUri
			secondaryToken = (await authStorage.readEntry(
				storageConstants.SECONDARY_TOKEN_TABLE,
				[secondaryUrl, authDomain, referenceUri]
			)) as SecondaryTokenTableEntry;
		} else {
			secondaryToken = (await authStorage.readEntry(
				storageConstants.SECONDARY_TOKEN_TABLE,
				[secondaryUrl, referenceUri],
				storageConstants.INDEX,
				storageConstants.SERVICE_URL_REFERENCE_URI
			)) as SecondaryTokenTableEntry;
		}

		if (secondaryToken) {
			//token found in SecondaryTokenTable for full URL
			if (!this.isTokenExpired(secondaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						':token found in SecondaryTokenTable for full URL: ' +
						utils.removeQueryStringAndHashFromUrl(secondaryUrl)
				);
				if (decrypt) {
					secondaryToken.token = await ctxsNative.Decrypt(
						referenceUri,
						secondaryToken.token
					);
				}
				return secondaryToken;
			}
			Logger.getInstance().write(
				logConstants.INFO,
				id +
					'Token for [' +
					secondaryToken.serviceURL +
					',' +
					secondaryToken.authDomain +
					'] in ' +
					storageConstants.SECONDARY_TOKEN_TABLE +
					' expired'
			);
			await this.deleteSecondaryToken(secondaryToken);
		}
		if (!authDomain) {
			const storeConfig = AuthMan.getInstance().getStoreConfiguration();
			const whitelistUrls = storeConfig[referenceUri]?.secondaryTokenWhitelistUrls;
			if (whitelistUrls && whitelistUrls.includes(secondaryUrl)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id + ' :secondaryUrl in whitelistUrls, returning null'
				);
				return null;
			}
			//checking if token for origin is found in SecondaryTokenTable since URL is not authDomain specific
			const originOfSecondaryUrl = utils.getOrigin(secondaryUrl);
			return await this.getCachedSecondaryTokenForOrigin(
				secondaryUrl,
				originOfSecondaryUrl,
				referenceUri,
				id,
				decrypt
			);
		}
		return null;
	}

	async getCachedSecondaryTokenForOrigin(
		secondaryUrl: string,
		originOfSecondaryUrl: string,
		referenceUri: string,
		id: string,
		decrypt = true
	): Promise<SecondaryTokenTableEntry> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		const secondaryToken = (await authStorage.readEntry(
			storageConstants.SECONDARY_TOKEN_TABLE,
			[originOfSecondaryUrl, referenceUri],
			storageConstants.INDEX,
			storageConstants.SERVICE_URL_REFERENCE_URI
		)) as SecondaryTokenTableEntry;
		if (secondaryToken) {
			if (this.isTokenExpired(secondaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						'Token for [' +
						secondaryToken.serviceURL +
						',' +
						secondaryToken.authDomain +
						'] in ' +
						storageConstants.SECONDARY_TOKEN_TABLE +
						' expired'
				);
				await this.deleteSecondaryToken(secondaryToken);
			} else {
				//store same token against origin for URLs which are not specific to auth domain
				await this.duplicateSecondaryTokenForNewServiceURL(secondaryUrl, secondaryToken);
				Logger.getInstance().write(
					logConstants.INFO,
					id + ':token found in SecondaryTokenTable for origin ' + originOfSecondaryUrl
				);
				if (decrypt) {
					secondaryToken.token = await ctxsNative.Decrypt(
						referenceUri,
						secondaryToken.token
					);
				}
				return secondaryToken;
			}
		}
		return null;
	}

	/// <summary>
	/// get secondary token if present in the db for given origin and realm (i.e formattedRealm) since token can be reused only if the realm and the audience are same (audience includes the scheme and authority (hostname + port) from the Url which is nothing but the origin)
	/// </summary>
	/// <param name="realm">realm for which we need a token</param>
	/// <param name="secondaryUrl">url for which we need a token</param>
	/// <param name="id">request id</param>
	/// <returns>object containing secondary token</returns>
	async getCachedSecondaryTokenForRealm(
		realm: string,
		secondaryUrl: string,
		id: string,
		referenceUri: string,
		decrypt = true
	): Promise<SecondaryTokenTableEntry | null> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		const origin = Utils.getInstance().getOrigin(secondaryUrl);
		const formattedRealm = AuthUtils.getInstance().formatRealmForService(realm, origin);
		const secondaryToken: SecondaryTokenTableEntry = (await authStorage.readEntry(
			storageConstants.SECONDARY_TOKEN_TABLE,
			[formattedRealm, referenceUri],
			storageConstants.INDEX,
			storageConstants.TOKEN_FOR_SERVICE_REFERENCE_URI
		)) as SecondaryTokenTableEntry;
		if (secondaryToken) {
			//token found in SecondaryTokenTable for realm
			if (this.isTokenExpired(secondaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						'Token for [' +
						secondaryToken.serviceURL +
						',' +
						secondaryToken.authDomain +
						'] in ' +
						storageConstants.SECONDARY_TOKEN_TABLE +
						' expired'
				);
				await this.deleteSecondaryToken(secondaryToken);
			} else {
				await this.duplicateSecondaryTokenForNewServiceURL(secondaryUrl, secondaryToken);
				Logger.getInstance().write(
					logConstants.INFO,
					id + ':token for same realm found in SecondaryTokenTable'
				);
				if (decrypt) {
					secondaryToken.token = await ctxsNative.Decrypt(
						secondaryToken.referenceUri,
						secondaryToken.token
					);
				}
				return secondaryToken;
			}
		}
		return null;
	}

	/// <summary>
	/// get primary token if present in the db for given auth domain
	/// deletes tokens and session cookies for the store if primary token has expired
	/// </summary>
	/// <param name="authDomain">auth domain</param>
	/// <param name="id">request id</param>
	/// <returns>object containing primary token</returns>
	async getCachedPrimaryTokenForAuthDomain(
		authDomain: string,
		id: string
	): Promise<PrimaryTokenTableEntry | null> {
		const authStorageWrapper = await AuthStorageWrapper.getInstance();
		const primaryToken: PrimaryTokenTableEntry = (await authStorageWrapper.readEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			authDomain,
			storageConstants.INDEX,
			storageConstants.AUTH_DOMAIN
		)) as PrimaryTokenTableEntry;
		if (primaryToken) {
			if (this.isTokenExpired(primaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						'Token for [' +
						primaryToken.authDomain +
						', ' +
						primaryToken.referenceUri +
						'] in ' +
						storageConstants.PRIMARY_TOKEN_TABLE +
						' expired'
				);
				await DSAuth.getInstance().deleteTokensAndCookiesForStore(
					primaryToken.referenceUri,
					primaryToken.authDomain
				);
			} else {
				primaryToken.token = await ctxsNative.Decrypt(
					primaryToken.referenceUri,
					primaryToken.token
				);
				return primaryToken;
			}
		}
		return null;
	}

	/// <summary>
	/// get primary token table entry for a given referenceUri
	/// </summary>
	/// <param name="referenceUri">discover url</param>
	/// <returns>PrimaryTokenTableEntry</returns>
	async getPrimaryTokenForStore(referenceUri: string): Promise<PrimaryTokenTableEntry> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		const primaryToken: PrimaryTokenTableEntry = (await authStorage.readEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			referenceUri,
			storageConstants.INDEX,
			storageConstants.REFERENCE_URI
		)) as PrimaryTokenTableEntry;
		return primaryToken;
	}

	/// <summary>
	/// get service url and auth domain for a given referenceUri
	/// deletes tokens and session cookies for the store if primary token has expired
	/// </summary>
	/// <param name="referenceUri">discover url</param>
	/// <returns>service URL and auth domain</returns>
	async getAndUpdateStoreInfo(
		referenceUri: string
	): Promise<Partial<PrimaryTokenTableEntry>> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		const primaryToken: PrimaryTokenTableEntry = (await authStorage.readEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			referenceUri,
			storageConstants.INDEX,
			storageConstants.REFERENCE_URI
		)) as PrimaryTokenTableEntry;
		if (primaryToken) {
			if (this.isTokenExpired(primaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					'Token for ' +
						primaryToken.referenceUri +
						' in ' +
						storageConstants.PRIMARY_TOKEN_TABLE +
						' expired'
				);
				await DSAuth.getInstance().deleteTokensAndCookiesForStore(
					primaryToken.referenceUri,
					primaryToken.authDomain
				);
			} else {
				return {
					serviceURL: primaryToken.serviceURL,
					authDomain: primaryToken.authDomain,
				};
			}
		}
		return {};
	}

	/// <summary>
	/// get primary token if present in the db for given referenceUri and auth domain
	/// deletes tokens and session cookies for the store if primary token has expired
	/// </summary>
	/// <param name="authDomain">auth domain</param>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="id">request id</param>
	/// <returns>object containing primary token</returns>
	async getCachedPrimaryToken(
		authDomain: string,
		referenceUri: string,
		id: string,
		decryptToken = true
	): Promise<PrimaryTokenTableEntry | null> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		const primaryToken: PrimaryTokenTableEntry = (await authStorage.readEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			[authDomain, referenceUri]
		)) as PrimaryTokenTableEntry;
		if (primaryToken) {
			if (!config.isDSAuthResiliencyEnabled && this.isTokenExpired(primaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						'Token for [' +
						primaryToken.authDomain +
						', ' +
						primaryToken.referenceUri +
						'] in ' +
						storageConstants.PRIMARY_TOKEN_TABLE +
						' expired'
				);
				await DSAuth.getInstance().deleteTokensAndCookiesForStore(
					primaryToken.referenceUri,
					primaryToken.authDomain
				);
			} else {
				if (decryptToken) {
					primaryToken.token = await ctxsNative.Decrypt(
						primaryToken.referenceUri,
						primaryToken.token
					);
				}
				return primaryToken;
			}
		}
		return null;
	}

	/// <summary>
	/// deletes primary token and secondary tokens of a given store using referenceUri and auth domain
	/// </summary>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="authDomain">auth domain of the store</param>
	async deleteTokensForStore(referenceUri: string, authDomain: string): Promise<void> {
		await this.deletePrimaryToken(referenceUri, authDomain);
		await this.deleteSecondaryTokensForStore(referenceUri, authDomain);
	}

	/// <summary>
	/// check if we have a primary token in the db for given serviceURL
	/// deletes tokens and session cookies for the store if primary token has expired
	/// </summary>
	/// <param name="serviceURL">primary token service url</param>
	/// <returns>true or false</returns>
	async hasCachedPrimaryTokenForServiceURL(
		id: string,
		serviceURL: string
	): Promise<boolean> {
		const authStorageWrapper = await AuthStorageWrapper.getInstance();
		const primaryToken: PrimaryTokenTableEntry = (await authStorageWrapper.readEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			serviceURL,
			storageConstants.INDEX,
			storageConstants.SERVICE_URL
		)) as PrimaryTokenTableEntry;
		if (primaryToken) {
			if (this.isTokenExpired(primaryToken)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						'Token for [' +
						primaryToken.authDomain +
						', ' +
						primaryToken.referenceUri +
						'] in ' +
						storageConstants.PRIMARY_TOKEN_TABLE +
						' expired'
				);
				await DSAuth.getInstance().deleteTokensAndCookiesForStore(
					primaryToken.referenceUri,
					primaryToken.authDomain
				);
			} else {
				return true;
			}
		}
		return false;
	}

	/// <summary>
	/// get all primary tokens present in the db
	/// </summary>
	/// <returns>array of objects containing primary tokens</returns>
	async getAllPrimaryTokens(): Promise<PrimaryTokenTableEntry[]> {
		const authStorageWrapper = await AuthStorageWrapper.getInstance();
		const primaryTokens: PrimaryTokenTableEntry[] = (await authStorageWrapper.readAll(
			storageConstants.PRIMARY_TOKEN_TABLE,
			storageConstants.TOKEN
		)) as PrimaryTokenTableEntry[];
		return primaryTokens;
	}

	async storePrimaryTokenEntry(
		primaryTokenTableEntryObj: PrimaryTokenTableEntry,
		encryptToken: boolean,
		reason: PrimaryTokensUpdateReason
	): Promise<void> {
		const { referenceUri, token } = primaryTokenTableEntryObj;
		const authStorage = await (
			await AuthStorageWrapper.getInstance()
		).getAuthStorage(referenceUri);
		if (encryptToken) {
			primaryTokenTableEntryObj['token'] = await ctxsNative.Encrypt(referenceUri, token);
		}
		await authStorage.updateEntry(
			storageConstants.PRIMARY_TOKEN_TABLE,
			primaryTokenTableEntryObj
		);
		await this.sendPrimaryTokenUpdateEventToNative(primaryTokenTableEntryObj, reason);
	}
	/// <summary>
	/// store primary token in primary token table
	/// </summary>
	/// <param name="primaryToken">primary token</param>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="serviceURL">service url</param>
	/// <returns>void</returns>
	async storePrimaryToken(
		primaryToken: RequestTokenResponse,
		referenceUri: string,
		reason: PrimaryTokensUpdateReason,
		storeAuthDomain = true
	): Promise<void> {
		const storeConfig = AuthMan.getInstance().getStoreConfiguration();
		const encryptedToken: string = await ctxsNative.Encrypt(
			referenceUri,
			primaryToken['token']
		);
		const primaryTokenTableEntry: PrimaryTokenTableEntry = {
			tokenForService: primaryToken['tokenForService'],
			tokenIssuedDate: primaryToken['tokenIssuedDate'],
			tokenExpiryDate: primaryToken['tokenExpiryDate'],
			tokenLifeTime: primaryToken['tokenLifeTime'],
			webLogoffUrl: primaryToken['webLogoffUrl'],
			token: encryptedToken,
			tokenExpiryMillis: primaryToken['tokenExpiryMillis'],
			referenceUri: referenceUri,
			authDomain: storeAuthDomain
				? AuthUtils.getInstance().getAuthDomainFromRealm(primaryToken['tokenForService'])
				: '',
			serviceURL: storeConfig[referenceUri].tokenServiceURL,
		};
		await this.storePrimaryTokenEntry(primaryTokenTableEntry, false, reason);
	}

	private async sendPrimaryTokenUpdateEventToNative(
		primaryTokenTableEntry: PrimaryTokenTableEntry,
		reason: PrimaryTokensUpdateReason
	) {
		try {
			const primaryTokensUpdatedEvent: PrimaryTokensUpdatedEvent = {
				id: v4(),
				type: EventType.UPDATED_PRIMARY_TOKENS,
				payload: {
					tokens: [primaryTokenTableEntry],
				},
				meta: {
					reason,
				},
			};
			environment.sendEventToNative(primaryTokensUpdatedEvent);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error occured while sending primary token update event: ${e.message}`
			);
		}
	}

	/// <summary>
	/// Store secondary token in secondary token table
	///
	/// Note about encryption:
	/// Tokens are encrypted and decrypted using Encrypt and Decrypt APIs provided by native (ctxsNative.Encrypt and ctxsNative.Decrypt)
	/// Encrypt and Decrypt API takes referenceUri and token as the two params, return back encrypted and decrypted token respectively
	/// referenceUri is used for additional entropy
	/// CWA Win is using DPAPI internally for the encryption
	/// </summary>
	async storeSecondaryToken(
		params: StoreSecondaryTokenFunctionParams,
		requestTokenResponse: RequestTokenResponse
	): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			params.referenceUri
		);
		const encryptedToken = await ctxsNative.Encrypt(
			params.referenceUri,
			requestTokenResponse['token']
		);
		const serviceURL = Utils.getInstance().removeQueryStringAndHashFromUrl(params.url);
		const origin = Utils.getInstance().getOrigin(serviceURL);

		const secondaryTokenTableEntry = {
			tokenForService: AuthUtils.getInstance().formatRealmForService(
				requestTokenResponse['tokenForService'],
				origin
			), //formattedRealm
			tokenIssuedDate: requestTokenResponse['tokenIssuedDate'],
			tokenExpiryDate: requestTokenResponse['tokenExpiryDate'],
			tokenLifeTime: requestTokenResponse['tokenLifeTime'],
			token: encryptedToken,
			tokenExpiryMillis: requestTokenResponse['tokenExpiryMillis'],
			authDomain: params.authChallenge.getAuthDomain(), //using authDomain from authChallenge here because authChallenge will always have authDomain where as the authDomain present in params is the one extracted from request url or headers which can sometimes be absent
			serviceURL: serviceURL,
			referenceUri: params.referenceUri,
		};
		//storing token against full URL
		await authStorage.updateEntry(
			storageConstants.SECONDARY_TOKEN_TABLE,
			secondaryTokenTableEntry
		);

		if (!params.authDomain) {
			const storeConfig = AuthMan.getInstance().getStoreConfiguration();
			const whitelistUrls = storeConfig[params.referenceUri]?.secondaryTokenWhitelistUrls;
			if (whitelistUrls && whitelistUrls.includes(serviceURL)) {
				Logger.getInstance().write(
					logConstants.INFO,
					'serviceURL in whitelistUrls, avoiding duplication of secondary token entries'
				);
				return null;
			}

			//storing the same token against origin of secondary url since url is not auth domain specific
			const originOfSecondaryUrl = utils.getOrigin(params.url);
			secondaryTokenTableEntry.serviceURL = originOfSecondaryUrl;
			await authStorage.updateEntry(
				storageConstants.SECONDARY_TOKEN_TABLE,
				secondaryTokenTableEntry
			);
		}
	}

	/// <summary>
	/// when we find a token for realm but not for the url, this method is called to create a new entry in the secondary token table for the new servie url.
	/// this is to make subsequent token look up for the new url faster
	/// </summary>
	async duplicateSecondaryTokenForNewServiceURL(
		secondaryUrl: string,
		secondaryTokenTableEntry: SecondaryTokenTableEntry
	): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			secondaryTokenTableEntry.referenceUri
		);

		const storeConfig = AuthMan.getInstance().getStoreConfiguration();
		const whitelistUrls =
			storeConfig[secondaryTokenTableEntry.referenceUri]?.secondaryTokenWhitelistUrls;

		if (whitelistUrls && whitelistUrls.includes(secondaryUrl)) {
			Logger.getInstance().write(
				logConstants.INFO,
				'secondaryUrl in whitelistUrls, avoiding duplication of secondary token entries'
			);
			return;
		}

		//creating a copy as serviceURL needs to be modified only for new table entry
		const newSecondaryTokenTableEntry = JSON.parse(
			JSON.stringify(secondaryTokenTableEntry)
		);
		newSecondaryTokenTableEntry.serviceURL =
			Utils.getInstance().removeQueryStringAndHashFromUrl(secondaryUrl);
		//storing token against full URL
		await authStorage.updateEntry(
			storageConstants.SECONDARY_TOKEN_TABLE,
			newSecondaryTokenTableEntry
		);
	}

	/// <summary>
	/// check if a token is expired
	/// </summary>
	/// <returns>true or false</returns>
	isTokenExpired(
		tokenObj: SecondaryTokenTableEntry | PrimaryTokenTableEntry | RequestTokenResponse
	): boolean {
		return tokenObj.tokenExpiryMillis < Date.now() ? true : false;
	}

	/// <summary>
	/// delete primary token corresponding to referenceUri and auth domain
	/// </summary>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="authDomain">auth domain of the store</param>
	async deletePrimaryToken(referenceUri: string, authDomain: string): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		Logger.getInstance().write(
			logConstants.INFO,
			'Deleting primary token corresponding to referenceUri ' +
				referenceUri +
				' , authDomain ' +
				authDomain
		);
		await authStorage.deleteEntry(storageConstants.PRIMARY_TOKEN_TABLE, [
			authDomain,
			referenceUri,
		]);
	}

	/// <summary>
	/// delete secondary token
	/// </summary>
	async deleteSecondaryToken(secondaryTokenObj: SecondaryTokenTableEntry): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			secondaryTokenObj.referenceUri
		);
		await authStorage.deleteEntry(storageConstants.SECONDARY_TOKEN_TABLE, [
			secondaryTokenObj.serviceURL,
			secondaryTokenObj.authDomain,
			secondaryTokenObj.referenceUri,
		]);
	}

	/// <summary>
	/// delete all secondary tokens for a given store using referenceUri and auth domain
	/// </summary>
	/// <param name="referenceUri">discovery url</param>
	/// <param name="authDomain">auth domain of the store</param>
	async deleteSecondaryTokensForStore(
		referenceUri: string,
		authDomain: string
	): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceUri
		);
		let secondaryTokens: SecondaryTokenTableEntry[];
		if (config.isUnifiedAuthenticationEnabled && !authDomain) {
			secondaryTokens = (await authStorage.readAll(
				storageConstants.SECONDARY_TOKEN_TABLE,
				storageConstants.REFERENCE_URI,
				referenceUri
			)) as SecondaryTokenTableEntry[];
		} else {
			secondaryTokens = (await authStorage.readAll(
				storageConstants.SECONDARY_TOKEN_TABLE,
				storageConstants.AUTH_DOMAIN_REFERENCE_URI,
				[authDomain, referenceUri]
			)) as SecondaryTokenTableEntry[];
		}

		if (secondaryTokens && Array.isArray(secondaryTokens)) {
			Logger.getInstance().write(
				logConstants.INFO,
				'Deleting secondary tokens corresponding to referenceUri ' +
					referenceUri +
					' , authDomain ' +
					authDomain
			);
			for (const tokenObj of secondaryTokens) {
				await authStorage.deleteEntry(storageConstants.SECONDARY_TOKEN_TABLE, [
					tokenObj.serviceURL,
					tokenObj.authDomain,
					tokenObj.referenceUri,
				]);
			}
		}
	}

	/// <summary>
	/// delete all secondary tokens for a given origin and realm (i.e formattedRealm).
	/// Examples:
	/// 1.	For HDX URL's such as "https://myworkprod0.cloud.com/Citrix/StoreWeb/CitrixAuth/Login" and "https://myworkprod0.cloud.com/Citrix/Store/resources/v2/" which are
	/// 	having same realm and origin so if token didn't work for one of the url, it wont work for both and this function will delete tokens for both from the DB.
	///
	/// 2.	For SaaS URL's such as "http://app.netscalergateway.net" and "https://config.netscalergateway.net/ngs/policy/getcfg.json" having same realm but different origin
	///		so if token for config.netscalergateway.net didn't work then this fuction will delete tokens for config.netscalergateway.net only from the DB.
	///
	/// </summary>
	/// <param name="realm">realm (also known as <for-service> attribute found in token response)</param>
	/// <param name="url">secondary url</param>
	/// sample realm: "1.StoreFrontProdRp1.y5xIpTnN9EKaEyayBvYg-g.token"
	async deleteSecondaryTokensForRealm(
		realm: string,
		url: string,
		referenceURI: string
	): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage(
			referenceURI
		);
		const origin = Utils.getInstance().getOrigin(url);
		const formattedRealm = AuthUtils.getInstance().formatRealmForService(realm, origin);
		const secondaryTokens: SecondaryTokenTableEntry[] = (await authStorage.readAll(
			storageConstants.SECONDARY_TOKEN_TABLE,
			storageConstants.TOKEN_FOR_SERVICE_REFERENCE_URI,
			[formattedRealm, referenceURI]
		)) as SecondaryTokenTableEntry[];
		if (secondaryTokens && Array.isArray(secondaryTokens)) {
			Logger.getInstance().write(
				logConstants.INFO,
				'Deleting secondary tokens corresponding to realm ' + realm
			);
			for (const tokenObj of secondaryTokens) {
				await authStorage.deleteEntry(storageConstants.SECONDARY_TOKEN_TABLE, [
					tokenObj.serviceURL,
					tokenObj.authDomain,
					tokenObj.referenceUri,
				]);
			}
		}
	}
}
